<template>
  <!-- D: 포커스시 is-open 클래스 추가 -->
  <!-- D: data-layer-hidden 속성 존재시 단순 검색 케이스로 바뀜 -->

  <!-- PC 영역 -->
  <div
    class="c-search only-pc"
    :class="{ 'is-open': isShowHelpLayer }"
    ref="cSearchEl"
    @focusin="() => (isFocusIn = true)"
  >
    <h2 class="sr-only">검색 영역</h2>
    <form class="search-form" @submit.prevent="search">
      <label class="sr-only">제품검색</label>
      <!-- D: MO에서는 input readonly 추가 필요합니다(레이어팝업 오픈하기 위함) -->
      <input
        type="text"
        title="검색"
        class="search-input"
        autocomplete="off"
        :placeholder="'제품명/제품코드 입력'"
        :value="keyword"
        @input="handleSearchInput"
      />
      <button type="button" class="search-cancel" @click="onRemoveKewordBtn">
        <i class="c-icon icon-large-x"></i>
        <span class="sr-only">검색어 재입력</span>
      </button>
      <button type="submit" class="search-submit">
        <span class="sr-only">검색</span>
      </button>
    </form>

    <!-- search layer -->
    <div class="search-layer" ref="searchLayerEl">
      <div class="search-layer-inner">
        <div v-show="!isShowAutoKeyword" class="c-text-tabs type-small">
          <ul>
            <li class="tab-item">
              <!-- D: is-active, 추가 활성화 -->
              <a
                href="#tab1-1"
                class="tab-anchor"
                :class="{ 'is-active': isShowLatestKeywordTab }"
                role="tab"
                aria-selected
                @click.prevent="() => (isShowLatestKeywordTab = true)"
              >
                최근검색어
              </a>
            </li>
            <li class="tab-item">
              <a
                href="#tab1-2"
                class="tab-anchor"
                :class="{ 'is-active': !isShowLatestKeywordTab }"
                role="tab"
                @click.prevent="() => (isShowLatestKeywordTab = false)"
              >
                인기검색어
              </a>
            </li>
          </ul>
        </div>

        <!-- D: 탭 전체 -->
        <div class="tab-panel-wrap" v-if="!isShowAutoKeyword">
          <!-- D: 탭 내용: 최근 검색어 -->
          <div
            v-if="!isShowAutoKeyword && isShowLatestKeywordTab"
            id="tab1-1"
            class="tab-panel"
            role="tabpanel"
          >
            <h3 class="sr-only">최근 검색어 탭 내용</h3>
            <RecentSearchesCont @select="onLatestKeywordSelect" />
          </div>
          <!-- D: 탭 내용: 인기 검색어 -->
          <div
            v-if="!isShowAutoKeyword && !isShowLatestKeywordTab"
            id="tab1-2"
            class="tab-panel"
            role="tabpanel"
          >
            <h3 class="sr-only">인기 검색어 탭 내용</h3>
            <PopularSearchCont @select="onLatestKeywordSelect" />
          </div>
        </div>

        <!-- D: 입력시 노출, 탭 숨김 -->
        <AutoKeywordList
          v-if="isShowAutoKeyword"
          :shwrd="keyword"
          @select="onLatestKeywordSelect"
        />
      </div>
      <button type="button" class="search-layer-close">
        <i class="c-icon icon-small-white-x"></i>
        <span>닫기</span>
      </button>
    </div>
  </div>
  <!-- // PC 영역 -->

  <!-- 모바일 영역 -->
  <div class="c-search only-mo">
    <h2 class="sr-only">검색 영역</h2>
    <form class="search-form" @submit.prevent="search">
      <label class="sr-only">제품검색</label>
      <!-- D: MO에서는 input readonly 추가 필요합니다(레이어팝업 오픈하기 위함) -->
      <input
        type="text"
        id="searchQuery"
        title="검색"
        class="search-input"
        autocomplete="off"
        :placeholder="'제품명/제품코드 입력'"
        :value="keyword"
        @input="handleSearchInput"
        @click="handleMobileKeywordClick"
      />
      <button type="button" class="search-cancel" @click="onRemoveKewordBtn">
        <i class="c-icon icon-large-x"></i>
        <span class="sr-only">검색어 재입력</span>
      </button>
      <button type="submit" class="search-submit">
        <span class="sr-only">검색</span>
      </button>
    </form>
  </div>
  <!-- // 모바일 영역 -->
  <SearchKeywordMoPop
    v-if="isShowMobileSearchKeywordPop"
    :keyword="keyword"
    @keyword-changed="mobileKeywordChanged"
    @close="() => (isShowMobileSearchKeywordPop = false)"
  />
</template>
<script setup>
import {
  ref,
  defineEmits,
  defineModel,
  computed,
  onMounted,
  onBeforeUnmount,
  inject,
  watch,
} from "vue";
import RecentSearchesCont from "@/components/paintpro/prod/recentSearchesCont.vue";
import PopularSearchCont from "@/components/paintpro/prod/popularSearchCont.vue";
import AutoKeywordList from "@/components/paintpro/prod/searchAutoKeywordList.vue";
import SearchKeywordMoPop from "@/components/paintpro/prod/searchKeywordMoPop.vue";
import Utils from "@/js/utils";
import { api } from "@/plugins/api";
import * as cmUtil from "@/utils/cmUtil.js";
const { isMobile } = cmUtil.useDeviceType();

const url = inject("url_prefix");
const emits = defineEmits(["keywordChanged"]);

// const isShowHelpLayer = ref(false); // 자동완성, 최근/인기 검색 레이어 표시 여부

const isFocusIn = ref(false);
const keyword = defineModel("keyword", { required: true });

const isShowLatestKeywordTab = ref(true); // 최근검색어탭 표시여부 true: 최근검색어탭/ false: 인기검색어탭

const cSearchEl = ref(); // 검색필드부분 (top, left, rigth 좌표에 필요)
const searchLayerEl = ref(); // 도우미 확장부분 (bottom 좌표에 필요)

const isShowMobileSearchKeywordPop = ref(false);

onMounted(() => {
  window.addEventListener("click", windowClickEvent);
});

onBeforeUnmount(() => {
  window.removeEventListener("click", windowClickEvent);
});

watch(keyword, () => {
  if (Utils.isNotBlank(keyword.value)) isFocusIn.value = true;
});

const search = () => {
  isFocusIn.value = false;

  saveAgencySrchHist();
  emits("keywordChanged", keyword.value.trim());
};

const isShowAutoKeyword = computed(() => {
  // 자동완성 키워드 표시여부 true: 자동완성 / false: 최근/인기 검색 레이어어
  return Utils.isNotBlank(keyword.value);
});

const isShowHelpLayer = computed(() => {
  return isFocusIn.value;
});

const onRemoveKewordBtn = () => {
  keyword.value = "";
};
const windowClickEvent = (event) => {
  if (
    event.x > cSearchEl.value.getBoundingClientRect().left &&
    event.x < cSearchEl.value.getBoundingClientRect().right &&
    event.y > cSearchEl.value.getBoundingClientRect().top &&
    event.y < searchLayerEl.value.getBoundingClientRect().bottom
  ) {
    isFocusIn.value = true;
  } else {
    isFocusIn.value = false;
  }
};

const saveAgencySrchHist = async () => {
  if (Utils.isBlank(keyword.value)) {
    return;
  }
  const payload = { shwrd: keyword.value==null?null:keyword.value.trim() };

  const res = await api.post(
    `${url}/agency/search/agency-srch-hist/create`,
    payload
  );

  const { resultCode } = res;
  if (resultCode !== 200) {
    return;
  }
};

const onLatestKeywordSelect = async (selectedKeyword) => {
  await setKeyword(selectedKeyword);
  search();
};

const setKeyword = async (_keyword) => {
  keyword.value = _keyword;
};

const handleMobileKeywordClick = () => {
  if (isMobile.value) {
    isShowMobileSearchKeywordPop.value = true;
  }
};

const mobileKeywordChanged = async (_keyword) => {
  await setKeyword(_keyword);
  search();
};

const handleSearchInput = (event) => {
  // 한글 입력 후 마우스 클릭 시 순간적으로 입력값이 없어지고 재 작성되는 현상때문에 setTimeout을 설정함
  setTimeout(() => {
    keyword.value = event.target.value;
  }, 10);
};
</script>
